<template>
  <div>
    <div class="main-content" v-if="loader === 100">
      <div class="card">
        <div class="card-body">
          <!-- Cabeçalho -->
          <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
            <div>
              <h2 class="mb-0">{{ manual.titulo }}</h2>
              <small class="text-muted" v-if="manual.updated_at">
                <i class="fa fa-clock-o"></i> Atualizado em {{ formatDate(manual.updated_at) }}
              </small>
            </div>
            <router-link to="/manuais" class="btn btn-outline-secondary">
              <i class="fa fa-arrow-left"></i> Voltar
            </router-link>
          </div>

          <!-- Descrição -->
          <p class="text-muted mt-1 mb-0" v-if="manual.descricao">{{ manual.descricao }}</p>

          <hr />

          <!-- Conteúdo (HTML rico) -->
          <div class="manual-conteudo" v-if="manual.conteudo" v-html="manual.conteudo"></div>
          <p class="text-muted" v-else>Este manual não possui conteúdo.</p>
        </div>
      </div>

      <!-- Arquivos -->
      <div class="card" v-if="manual.arquivos && manual.arquivos.length">
        <div class="card-header">
          <h4 class="mb-0">
            <i class="fa fa-paperclip"></i>
            Arquivos
            <span class="badge badge-primary ml-1">{{ manual.arquivos.length }}</span>
          </h4>
        </div>
        <div class="card-body">
          <div
            v-for="arquivo in manual.arquivos"
            :key="arquivo.id"
            class="arquivo-item mb-2"
          >
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div class="d-flex align-items-center">
                <i :class="['fa', 'fa-2x', 'mr-1', iconFor(arquivo)]"></i>
                <div>
                  <strong>{{ arquivo.arquivo_original || arquivo.arquivo }}</strong>
                  <br />
                  <small class="text-muted">
                    {{ (arquivo.extensao || '').toUpperCase() }}
                    <span v-if="arquivo.tamanho"> · {{ formatSize(arquivo.tamanho) }}</span>
                  </small>
                </div>
              </div>
              <div class="mt-1 mt-md-0">
                <button
                  v-if="canPreview(arquivo)"
                  type="button"
                  class="btn btn-sm btn-outline-primary mr-1"
                  @click="togglePreview(arquivo.id)"
                >
                  <i :class="['fa', isOpen(arquivo.id) ? 'fa-eye-slash' : 'fa-eye']"></i>
                  {{ isOpen(arquivo.id) ? 'Ocultar' : 'Visualizar' }}
                </button>
                <a
                  :href="arquivo.arquivo_link"
                  target="_blank"
                  class="btn btn-sm btn-primary"
                >
                  <i class="fa fa-download"></i> Abrir / Baixar
                </a>
              </div>
            </div>

            <!-- Preview inline (somente ao clicar em Visualizar) -->
            <div class="preview mt-1" v-if="isOpen(arquivo.id)">
              <iframe
                v-if="isPdf(arquivo)"
                :src="pdfSrc(arquivo)"
                class="w-100"
                style="height: 85vh; border: 1px solid #ddd; border-radius: 6px;"
              ></iframe>
              <div class="text-center" v-else-if="isVideo(arquivo)">
                <video
                  :src="arquivo.arquivo_link"
                  class="w-100 bg-dark"
                  style="max-height: 70vh; border-radius: 6px;"
                  controls
                ></video>
              </div>
              <div class="text-center" v-else-if="isImage(arquivo)">
                <img
                  :src="arquivo.arquivo_link"
                  class="img-fluid"
                  style="max-height: 70vh; border-radius: 6px;"
                />
              </div>
            </div>

            <hr />
          </div>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div class="row justify-content-center mt-3" v-else>
      <div class="col-10 text-center">
        <div class="progress">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="'width: ' + loader + '%;'"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <b style="font-size: 14pt">Carregando</b><br />
        <b style="font-size: 16pt; line-height: 12pt">{{ loader }}%</b>
      </div>
    </div>
  </div>
</template>

<script>
const VIDEO_EXT = ["mp4", "webm", "mov", "ogg", "ogv", "m4v"];
const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

export default {
  name: "ManualView",
  data() {
    return {
      loader: 0,
      manual: {},
      opened: {},
    };
  },
  methods: {
    togglePreview: function (id) {
      this.$set(this.opened, id, !this.opened[id]);
    },
    isOpen: function (id) {
      return !!this.opened[id];
    },
    canPreview: function (arquivo) {
      return this.isPdf(arquivo) || this.isVideo(arquivo) || this.isImage(arquivo);
    },
    pdfSrc: function (arquivo) {
      // view=FitH = ajusta o PDF à largura do iframe (letra maior/legível)
      return arquivo.arquivo_link + "#view=FitH&zoom=page-width";
    },
    getManual: function (id) {
      const self = this;
      const api = self.$store.state.api + "manuais/" + id;

      axios
        .get(api, { params: { with: ["arquivos"] } })
        .then((response) => {
          self.manual = response.data.data[0] || {};
          self.setLoader(50);
        })
        .catch((error) => {
          self.$message(null, error.response ? error.response.data : error, "error");
        });
    },
    ext: function (arquivo) {
      return (arquivo.extensao || "").toString().toLowerCase();
    },
    isPdf: function (arquivo) {
      return this.ext(arquivo) === "pdf";
    },
    isVideo: function (arquivo) {
      return VIDEO_EXT.includes(this.ext(arquivo));
    },
    isImage: function (arquivo) {
      return IMAGE_EXT.includes(this.ext(arquivo));
    },
    iconFor: function (arquivo) {
      if (this.isPdf(arquivo)) return "fa-file-pdf-o text-danger";
      if (this.isVideo(arquivo)) return "fa-file-video-o text-info";
      if (this.isImage(arquivo)) return "fa-file-image-o text-success";
      return "fa-file-o text-secondary";
    },
    formatSize: function (bytes) {
      if (!bytes) return "";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      let size = bytes;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return size.toFixed(i === 0 ? 0 : 1) + " " + units[i];
    },
    formatDate: function (value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const pad = (n) => String(n).padStart(2, "0");
      return (
        pad(d.getDate()) +
        "/" +
        pad(d.getMonth() + 1) +
        "/" +
        d.getFullYear() +
        " " +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
      );
    },
    setLoader: function (qtd) {
      const self = this;
      let load = 0;
      let interval = setInterval(function () {
        self.loader++;
        load++;
        if (load == qtd) {
          clearInterval(interval);
        }
      }, 15);
    },
  },
  mounted: function () {
    const self = this;
    let id = self.$route.params.id;

    self.setLoader(50);

    if (id) {
      self.getManual(id);
    }
  },
};
</script>

<style scoped>
.manual-conteudo {
  line-height: 1.6;
}
.manual-conteudo >>> img {
  max-width: 100%;
  height: auto;
}
.manual-conteudo >>> table {
  width: 100%;
  border-collapse: collapse;
}
.manual-conteudo >>> td,
.manual-conteudo >>> th {
  border: 1px solid #ddd;
  padding: 6px;
}
.arquivo-item:last-child hr {
  display: none;
}
</style>
